import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./services/authService.jsx";
import Community from "./pages/Community.jsx";
import Login from "./pages/Login.jsx";
import Subscribe from "./pages/Subscribe.jsx";
import FAQPage from "./pages/FAQPage.jsx";
import AboutPage from "./pages/AboutPage.jsx";
import LandingPage from "./pages/LandingPage.jsx";
import UserProfile from "./pages/UserProfile.jsx";
import HorrorTree from "./pages/HorrorTree.jsx";
import CookieTest from "./pages/CookieTest.jsx";
import BlogPage from "./pages/Blog.jsx";
import BlogPostPage from "./pages/BlogPost.jsx";
import Shop from "./pages/Shop.jsx";
import TermsPage from "./pages/TermsPage.jsx";
import PrivacyPage from "./pages/PrivacyPage.jsx";
import Billing from "./pages/Billing.jsx";
import Dashboard from "./pages/Dashboard.jsx";
import ProtectedRoute from "./components/ProtectedRoute.jsx";
import Join from "./pages/Join.jsx";
import Invest from "./pages/Invest.jsx";
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import analyticsService from "./services/analyticsService.js";
import { useEffect } from "react";
import { Amplify } from 'aws-amplify';
import authConfig from './config/auth.config';
import PaymentSuccess from "./pages/PaymentSuccess.jsx";

// Create a theme instance with focus style overrides
const theme = createTheme({
  components: {
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&:focus': {
            outline: 'none',
          },
        },
      },
    },
  },
});

// AppContent component to use the useLocation hook (which must be inside Router)
const AppContent = () => {
  const location = useLocation();
  
  // Track page views when the location changes
  useEffect(() => {
    // Get page title from the document
    const pageTitle = document.title.replace(/™/g, '').trim() || 'Screamify';
    
    // Normalize page title to avoid duplicates
    const normalizedTitle = pageTitle === 'Screamify™' ? 'Screamify' : pageTitle;
    
    // Track page view with normalized title
    analyticsService.trackPageView(location.pathname, normalizedTitle);
  }, [location]);
  
  return (
    <AuthProvider>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/paymentsuccess" element={<PaymentSuccess />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/invest" element={<Invest />} />
        <Route path="/join" element={<Join />} />
        <Route path="/subscribe" element={<Subscribe />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/community" element={<Community />} />
        <Route path="/profile" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />
        <Route path="/horrortree" element={<ProtectedRoute><HorrorTree /></ProtectedRoute>} />
        <Route path="/horrortree/@:userhandle" element={<HorrorTree />} />
        <Route path="/horrortree/:userhandle" element={<HorrorTree />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/cookie-test" element={<CookieTest />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ToastContainer position="bottom-right" theme="dark" />
    </AuthProvider>
  );
};

function App() {
  // Configure Amplify ONCE when the App component mounts
  useEffect(() => {
    try {
      Amplify.configure({
        Auth: {
          Cognito: {
            userPoolId: authConfig.Auth.userPoolId,
            userPoolClientId: authConfig.Auth.userPoolWebClientId,
            cookieStorage: authConfig.Auth.cookieStorage,
            loginWith: { email: true },
            // oauth: authConfig.Auth.oauth // Include if needed
          }
        },
        // region: authConfig.Auth.region // Include if needed
      });
      console.log("Amplify configured successfully in App.jsx");
    } catch (configError) {
      console.error("Error configuring Amplify in App.jsx:", configError);
    }
  }, []); // Empty dependency array ensures this runs only once

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
}

export default App;